import { useState } from "react";
import { createPortal } from "react-dom";
import { Printer, Trash2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useApp } from "@/lib/app-context";
import { inr } from "@/lib/api";
import { PageHeader, Panel, Field, NativeSelect, Empty } from "@/components/common";
import { ProductPicker } from "@/components/ProductPicker";
import { Barcode } from "@/components/Barcode";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

const SIZES = { "50x25": [50, 25], "38x25": [38, 25], "58x40": [58, 40], "100x50": [100, 50] };

function withCheck(base) {
  const d = base.split("").map(Number).reverse();
  const c = (10 - (d.reduce((s, x, i) => s + x * (i % 2 === 0 ? 3 : 1), 0) % 10)) % 10;
  return base + c;
}

export function weightedCode(p, weightKg, price, s) {
  if (!s?.enabled || !p.plu_code) return null;
  const val = s.value_type === "weight" ? weightKg : price;
  const v = String(Math.round(val * 10 ** s.decimals)).padStart(s.value_digits, "0").slice(-s.value_digits);
  const base = s.prefix + String(p.plu_code).padStart(s.item_digits, "0").slice(-s.item_digits) + v;
  return withCheck(base);
}

const Label = ({ it, size, opts, s }) => {
  const [w, h] = SIZES[size];
  const loose = it.weight > 0;
  const price = loose ? +(it.weight * it.effective_offline_price).toFixed(2) : it.effective_offline_price;
  const code = loose ? weightedCode(it, it.weight, price, s) : it.barcode || it.erp_code;
  return (
    <div style={{ width: `${w}mm`, height: `${h}mm`, padding: "1.5mm", boxSizing: "border-box", overflow: "hidden", pageBreakAfter: "always", fontFamily: "DM Sans, sans-serif", color: "#000", background: "#fff", border: "1px dashed #ccc" }}>
      <div style={{ fontWeight: 700, fontSize: h > 30 ? 12 : 9, lineHeight: 1.1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{it.name}</div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: h > 30 ? 11 : 8 }}>
        <span>{loose ? `${it.weight} kg @ ${inr(it.effective_offline_price)}/kg` : `MRP ${inr(it.mrp)}`}</span><b>{inr(price)}</b>
      </div>
      {opts.batch && (it.batch || it.expiry) && <div style={{ fontSize: 7 }}>{it.batch && `B:${it.batch} `}{it.expiry && `EXP:${it.expiry}`}</div>}
      <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 1 }}>
        <Barcode value={code} height={h > 30 ? 30 : 16} width={w > 50 ? 1.4 : 1} fontSize={8} />
        {opts.qr && <QRCodeSVG value={it.erp_code} size={h > 30 ? 40 : 28} />}
      </div>
    </div>
  );
};

export default function Labels() {
  const { settings } = useApp();
  const [items, setItems] = useState([]);
  const [size, setSize] = useState("50x25");
  const [opts, setOpts] = useState({ batch: false, qr: false });
  const s = settings?.weighted_barcode;
  const upd = (i, k, v) => setItems(items.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const all = items.flatMap((it) => Array.from({ length: Math.max(1, Number(it.copies) || 1) }, () => ({ ...it, weight: Number(it.weight) || 0 })));
  const print = () => {
    const [w, h] = SIZES[size];
    const st = document.createElement("style");
    st.innerHTML = `@page { size: ${w}mm ${h}mm; margin: 0; }`;
    document.head.appendChild(st);
    setTimeout(() => { window.print(); st.remove(); }, 50);
  };
  return (
    <div>
      <PageHeader title="Labels & Barcodes" subtitle="Shelf and loose-item labels for thermal label printers, with print preview and reprint">
        <Button onClick={print} disabled={!items.length} data-testid="print-labels-button"><Printer className="h-4 w-4 mr-1" />Print {all.length} labels</Button>
      </PageHeader>
      <div className="grid lg:grid-cols-2 gap-4">
        <Panel title="Labels to print">
          <ProductPicker testid="label-product-picker" onPick={(p) => setItems([...items, { ...p, copies: 1, weight: "", batch: "", expiry: "" }])} />
          <div className="grid grid-cols-2 gap-3 my-3">
            <Field label="Label size (mm)"><NativeSelect testid="label-size-select" value={size} onChange={setSize} options={Object.keys(SIZES).map((k) => ({ value: k, label: k }))} /></Field>
            <div className="flex items-end gap-4 text-sm"><label className="flex gap-2 items-center"><Switch checked={opts.batch} onCheckedChange={(v) => setOpts({ ...opts, batch: v })} />Batch/expiry</label>
              <label className="flex gap-2 items-center"><Switch checked={opts.qr} onCheckedChange={(v) => setOpts({ ...opts, qr: v })} />QR</label></div>
          </div>
          {items.length ? items.map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center border-t py-2 text-sm" data-testid={`label-item-${i}`}>
              <span className="col-span-4 font-medium">{it.name}{!it.barcode && !it.is_loose && <div className="text-[10px] text-amber-600">no barcode — ERP ID used</div>}</span>
              <Input className="col-span-2 h-8" type="number" placeholder="copies" value={it.copies} onChange={(e) => upd(i, "copies", e.target.value)} data-testid={`label-copies-${i}`} />
              <Input className="col-span-2 h-8" type="number" placeholder={it.is_loose ? "kg" : "—"} disabled={!it.is_loose} value={it.weight} onChange={(e) => upd(i, "weight", e.target.value)} data-testid={`label-weight-${i}`} />
              <Input className="col-span-2 h-8" placeholder="batch" value={it.batch} onChange={(e) => upd(i, "batch", e.target.value)} />
              <Input className="col-span-1 h-8 px-1" type="date" value={it.expiry} onChange={(e) => upd(i, "expiry", e.target.value)} />
              <Button size="icon" variant="ghost" className="col-span-1" onClick={() => setItems(items.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
            </div>)) : <Empty text="Add products to print labels" />}
          <p className="text-xs text-muted-foreground mt-3">Loose items need a PLU code. Weighted barcodes use the format configured in Settings (prefix {s?.prefix}, {s?.item_digits}-digit PLU, {s?.value_digits}-digit {s?.value_type}) and are validated at checkout.</p>
        </Panel>
        <Panel title="Print preview">
          <div className="flex flex-wrap gap-3 bg-slate-100 p-4 rounded-lg min-h-[200px]" data-testid="label-preview">{all.map((it, i) => <Label key={i} it={it} size={size} opts={opts} s={s} />)}</div>
        </Panel>
      </div>
      {createPortal(<div className="print-area">{all.map((it, i) => <Label key={i} it={it} size={size} opts={opts} s={s} />)}</div>, document.body)}
    </div>
  );
}
